/**
 * StudioLabel 组件
 * 在地图上显示 Studio 信息标签（无背景卡片，平铺文字）
 * 包含：logo + 名称 + 星级、年份 + 国家 + 员工数、游戏图标（圆形缩略图 + overflow badge）、引擎标签
 *
 * Figma 设计要点：
 * - name: 14px bold #262832
 * - meta: 12px medium #838697
 * - 游戏行：游戏图标 + 圆形 16px 缩略图，超出 MAX_VISIBLE 个显示 +N 蓝色 badge
 * - 引擎行：引擎图标 + 16px 引擎 tag icons
 * - hover 时显示 dark card tooltip (#262832) 列出所有游戏名
 */

import React, { useMemo } from 'react';
import { IStudio, COUNTRY_FULL_NAME } from '../../../worldMap/const/studio';
import { STUDIO_ICON_MAP } from '../../../worldMap/const/iconMap';
import { GAME_ICON_MAP, ENGINE_ICON_MAP } from '../../../worldMap/const/gameMap';
import RatingIcon from '@/pages/dashboard/assets/svg/sth/star.svg';
import GameIcon from '@/pages/dashboard/assets/svg/sth/games.svg';
import EngineIcon from '@/pages/dashboard/assets/svg/sth/engine.svg';
import InfoIcon from '@/pages/dashboard/assets/svg/sth/infomation.svg';
import './index.scss';
import CommonBubble from '@home/components/CommonBubble';

interface IStudioLabelProps {
  studio: IStudio;
  position: { x: number; y: number; };
  /** Horizontal line data for the connector between row1 and row2 */
  lineData?: {
    /** Width of the horizontal segment (px) */
    width: number;
    /** Left offset relative to the label origin (px), negative when line starts left of label */
    offsetX: number;
  };
}

/** 游戏行最多显示的图标个数，超出显示 +N */
const MAX_VISIBLE_GAMES = 3;

/** 引擎行最多显示的图标个数 */
const MAX_VISIBLE_ENGINES = 3;

/**
 * 格式化员工数（加千位逗号）
 */
const formatEmployeeCount = (count?: number): string => {
  if (count === undefined || count === null) return '-';
  return count.toLocaleString();
};

/**
 * 渲染星级评分（支持半星）
 */
const StarRating: React.FC<{ rating: number; }> = ({ rating }) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;

  for (let i = 0;i < fullStars;i++) {
    stars.push(
      <img
        key={`full-${i}`}
        src={RatingIcon}
        alt="star"
        className="dbd-studio-label__star"
      />,
    );
  }

  if (hasHalfStar) {
    stars.push(
      <div key="half" className="dbd-studio-label__half-star">
        <img src={RatingIcon} alt="half-star" />
      </div>,
    );
  }

  return <div className="dbd-studio-label__stars">{stars}</div>;
};

/**
 * 解析 games 字符串为独立游戏名数组
 * 支持分号 / 斜杠分隔，例如 "Warframe(multi-platform); Soulframe"
 */
const parseGames = (games?: string[]): string[] => {
  if (!games || games.length === 0) return [];
  const result: string[] = [];
  games.forEach((g) => {
    g.split(/[;/]/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((s) => result.push(s));
  });
  return result;
};


const StudioLabel: React.FC<IStudioLabelProps> = ({ studio, position, lineData }) => {
  const {
    name,
    est,
    country,
    employee_count: employeeCountRaw,
    games,
    game_engine: gameEngine,
    ratings,
    studio_info: studioInfo,
    sub_studio: subStudio,
    employee_info: employeeInfo
  } = studio;

  const employeeCount = useMemo(
    () => formatEmployeeCount(employeeCountRaw),
    [employeeCountRaw],
  );
  const engines = gameEngine || [];
  const parsedGames = useMemo(() => parseGames(games), [games]);
  const displayCountry = COUNTRY_FULL_NAME[country] || country;
  const logoSrc = STUDIO_ICON_MAP[name];
  const visibleGames = parsedGames.slice(0, MAX_VISIBLE_GAMES);
  const overflowCount = parsedGames.length - MAX_VISIBLE_GAMES;

  const visibleEngines = engines.slice(0, MAX_VISIBLE_ENGINES);

  // 是否有 sub_studio（如 Shiftup+Yongxing）
  const hasSubStudio = !!subStudio;

  return (
    <div
      className="dbd-studio-label"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      {hasSubStudio ? (
        <>
          {/* Row 1: [sub_logo + sub_name + stars] ... [main_logo + main_name] */}
          <CommonBubble
            content={
              studioInfo ?
                <div className="dbd-studio-label__tooltip-content">
                  {studioInfo}
                </div> : ''
            }
            placement="top"
          >
            <div className="dbd-studio-label__row1 dbd-studio-label__row1--split">

              <div className="dbd-studio-label__row1-left">
                <img
                  className="dbd-studio-label__logo"
                  src={STUDIO_ICON_MAP[subStudio.name]}
                  alt={subStudio.name}
                />

                <span className="dbd-studio-label__name">{subStudio.name}</span>

              </div>
              <div className="dbd-studio-label__row1-right">
                <img
                  className="dbd-studio-label__logo"
                  src={logoSrc}
                  alt={name}
                />
                <span className="dbd-studio-label__name dbd-studio-label__name--sub">{name}</span>
                <StarRating rating={ratings} />
              </div>

            </div>
          </CommonBubble>
          {/* Horizontal connector line */}
          {lineData && lineData.width > 0 && (
            <div
              className="dbd-studio-label__hline"
              style={{
                width: `${lineData.width}px`,
                marginLeft: `${lineData.offsetX}px`,
              }}
            />
          )}

          {/* Row 2: [sub: year country | employee] ... [main: year country | employee] */}
          <div className="dbd-studio-label__row2 dbd-studio-label__row2--split">
            <div className="dbd-studio-label__row2-group">
              <span className="dbd-studio-label__meta">
                {subStudio.est} {COUNTRY_FULL_NAME[subStudio.country] || subStudio.country}
              </span>
              <span className="dbd-studio-label__divider">|</span>
              <CommonBubble content={subStudio.employee_info}>
                <div className='dbd-studio-label__employee'>
                  <img
                    className="dbd-studio-label__icon-sm"
                    src={InfoIcon}
                    alt="employees"
                  />
                  <span className="dbd-studio-label__meta">
                    {formatEmployeeCount(subStudio.employee_count)}
                  </span>
                </div>
              </CommonBubble>
            </div>
            <span className="dbd-studio-label__divider">|</span>
            <div className="dbd-studio-label__row2-group">
              <span className="dbd-studio-label__meta">
                {est} {displayCountry}
              </span>
              <span className="dbd-studio-label__divider">|</span>
              <CommonBubble content={employeeInfo}>
                <div className='dbd-studio-label__employee'>
                  <img
                    className="dbd-studio-label__icon-sm"
                    src={InfoIcon}
                    alt="employees"
                  />
                  <span className="dbd-studio-label__meta">{employeeCount}</span>
                </div>
              </CommonBubble>

            </div>
          </div>
        </>
      ) : (
        <>
          {/* Row 1: Logo + Name + Stars */}
          <div className="dbd-studio-label__row1">
            <img
              className="dbd-studio-label__logo"
              src={logoSrc}
              alt={name}
            />
            <CommonBubble
              content={
                studioInfo ?
                  <div className="dbd-studio-label__tooltip-content">
                    {studioInfo}
                  </div> : ''
              }
              placement="top"
            >
              <span className="dbd-studio-label__name">{name}</span>
            </CommonBubble>
            <StarRating rating={ratings} />
          </div>

          {/* Horizontal connector line */}
          {lineData && lineData.width > 0 && (
            <div
              className="dbd-studio-label__hline"
              style={{
                width: `${lineData.width}px`,
                marginLeft: `${lineData.offsetX}px`,
              }}
            />
          )}

          {/* Row 2: Year + Country | Employee count */}
          <div className="dbd-studio-label__row2">
            <span className="dbd-studio-label__meta">
              {est} {displayCountry}
            </span>
            <span className="dbd-studio-label__divider">|</span>
            <CommonBubble content={employeeInfo} className='dbd-studio-label__row2--split'>
              <div className='dbd-studio-label__employee'>
                <img
                  className="dbd-studio-label__icon-sm"
                  src={InfoIcon}
                  alt="employees"
                />
                <span className="dbd-studio-label__meta">{employeeCount}</span>
              </div>
            </CommonBubble>
          </div>
        </>
      )}

      {/* Row 3: Games (circular icons + overflow badge) | Engines (icon tags) */}

      <div className="dbd-studio-label__row3">
        {parsedGames.length > 0 && (
          <>
            <img
              className="dbd-studio-label__icon-sm"
              src={GameIcon}
              alt="games"
            />
            <div className="dbd-studio-label__items">
              {visibleGames.map((game, idx) => (
                <CommonBubble key={idx} content={game} placement="bottom">
                  <div className="dbd-studio-label__game-circle">
                    <img
                      className="dbd-studio-label__game-circle-img"
                      src={GAME_ICON_MAP[game] || GameIcon}
                      alt={game}
                    />
                  </div>
                </CommonBubble>
              ))}
              {overflowCount > 0 && (
                <CommonBubble
                  content={
                    <div className="dbd-studio-label__overflow-list">
                      {parsedGames.slice(MAX_VISIBLE_GAMES).map((game, idx) => (
                        <div key={idx} className="dbd-studio-label__overflow-item">{game}</div>
                      ))}
                    </div>
                  }
                  placement="bottom"
                >
                  <div className="dbd-studio-label__game-overflow">
                    <span>+{overflowCount}</span>
                  </div>
                </CommonBubble>
              )}
            </div>
          </>
        )}
        {/* Divider between games and engines */}
        {parsedGames.length > 0 && visibleEngines.length > 0 && (
          <span className="dbd-studio-label__divider">|</span>
        )}
        {visibleEngines.length > 0 && (
          <>
            <img
              className="dbd-studio-label__icon-sm"
              src={EngineIcon}
              alt="engine"
            />
            <div className="dbd-studio-label__items">
              {visibleEngines.map((engine) => (
                <CommonBubble key={engine} content={engine} placement="bottom">
                  <div className="dbd-studio-label__engine-tag">
                    <img
                      className="dbd-studio-label__engine-tag-img"
                      src={ENGINE_ICON_MAP[engine] || EngineIcon}
                      alt={engine}
                    />
                  </div>
                </CommonBubble>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(StudioLabel);
